import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  Paper,
  Rating,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import {
  PublicProfileProvider,
  usePublicProfile,
} from '../context/PublicProfileContext';
import GalleryCarousel from './components/GalleryCarousel';
import BookingModal from './components/BookingModal';
import { CreateBookingSlot } from '../types/bookingSlot';
import { getOpenStatus } from '../utils/businessHoursUtils';
import { getMockRatingSummary } from '../types/review';
import {
  BookableService,
  Booking,
  BookingContactDetails,
} from '../types/booking';
import { createBooking } from '../middleware/reservation_service/createRequest';
import { darkPageBg, darkCardBg } from './components/darkFormStyles';
import { companyName } from '../defaults/AppDefaults';
import StickyFooter from './components/StickyFooter';
import SampleDataBadge from './components/SampleDataBadge';

const accent = '#dd8709';

const PublicBarberProfileContent: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    loading,
    error,
    barber,
    galleryImages,
    barberServices,
    completedBookingsCount,
    getSlots,
    removeSlotFromCache,
  } = usePublicProfile();
  const [bookingOpen, setBookingOpen] = useState(false);
  const [preselected, setPreselected] = useState<BookableService | null>(null);

  // Only shown when arriving via a click-through from the Find Barbers
  // search results page, so users can easily return to their results.
  const cameFromSearch = Boolean(
    (location.state as { fromSearch?: boolean } | null)?.fromSearch
  );

  const openStatus = useMemo(
    () =>
      getOpenStatus(
        barber?.businessHours,
        barber?.openingHours,
        barber?.closingHours
      ),
    [barber]
  );

  const ratingSummary = useMemo(
    () => (barber?.id ? getMockRatingSummary(barber.id) : null),
    [barber?.id]
  );

  const services: BookableService[] = useMemo(
    () => barberServices.map((service) => ({ service })),
    [barberServices]
  );

  const address = [
    barber?.addressLineOne,
    barber?.addressLineTwo,
    barber?.city,
    barber?.postcode,
  ]
    .filter(Boolean)
    .join(', ');

  const barberName = `${barber?.firstName || ''}`.trim();

  const openBookingModal = (service?: BookableService) => {
    setPreselected(service ?? null);
    setBookingOpen(true);
  };

  const handleConfirmBooking = async (
    slot: CreateBookingSlot,
    bookable: BookableService,
    contact: BookingContactDetails
  ) => {
    const reservationData: Booking = {
      slot_id: slot.id,
      customer_email: contact.customerEmail,
      customer_phone: contact.customerPhone,
      customer_name: contact.customerName,
      barber_name: barberName,
      barber_email_address: barber?.email,
      service_name: bookable.service.name,
      price: bookable.service.price,
      status: 'PENDING',
      type: 'BARBER',
      barber_id: slot.barber_id,
    };

    await createBooking(reservationData);
    removeSlotFromCache(slot.id);
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: darkPageBg,
        }}
      >
        <CircularProgress sx={{ color: accent }} />
      </Box>
    );
  }

  if (error || !barber) {
    return (
      <Container sx={{ mt: '100px', pb: 6 }}>
        <Alert severity="error">
          {error || 'This barber profile could not be found.'}
        </Alert>
      </Container>
    );
  }

  return (
    <Box sx={{ bgcolor: darkPageBg, minHeight: '100vh', color: '#fff' }}>
      {/* Cover / Header */}
      <Box
        sx={{
          position: 'relative',
          height: { xs: 220, sm: 300 },
          backgroundImage: `linear-gradient(rgba(8,9,12,0.5), rgba(8,9,12,0.85)), url(${
            barber.profileImageSelfLink || ''
          })`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          mt: '64px',
        }}
      >
        {cameFromSearch && (
          <Button
            onClick={() => navigate('/find-barbers')}
            startIcon={<ArrowBackIcon />}
            sx={{
              position: 'absolute',
              top: 16,
              left: 16,
              color: '#fff',
              bgcolor: 'rgba(0,0,0,0.45)',
              textTransform: 'none',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.65)' },
            }}
          >
            Back to Results
          </Button>
        )}
        <Container
          sx={{
            height: '100%',
            display: 'flex',
            alignItems: 'flex-end',
            pb: 3,
          }}
        >
          <Avatar
            src={barber.profileImageSelfLink}
            sx={{ width: 96, height: 96, border: '3px solid #fff', mr: 2 }}
          >
            <ContentCutIcon />
          </Avatar>
          <Box>
            <Typography variant="h4" fontWeight="bold">
              {barberName || 'Independent Barber'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 0.5 }}>
              {barber.phoneNumber && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <PhoneIcon fontSize="small" />
                  <Typography variant="body2">{barber.phoneNumber}</Typography>
                </Box>
              )}
              {address && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <LocationOnIcon fontSize="small" />
                  <Typography variant="body2">{address}</Typography>
                </Box>
              )}
              {openStatus.todayHours && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <AccessTimeIcon fontSize="small" />
                  <Typography variant="body2">
                    {openStatus.todayHours}
                  </Typography>
                </Box>
              )}
              <Chip
                size="small"
                label={openStatus.isOpen ? 'Open Now' : 'Closed'}
                sx={{
                  bgcolor: openStatus.isOpen ? '#2e7d32' : '#616161',
                  color: '#fff',
                  fontWeight: 'bold',
                }}
              />
            </Box>
          </Box>
        </Container>
      </Box>

      <Container sx={{ py: 4 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={8}>
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
              Gallery
            </Typography>
            <GalleryCarousel images={galleryImages} />

            <Divider sx={{ my: 4, borderColor: 'rgba(255,255,255,0.1)' }} />

            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
              Services
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {services.length === 0 && (
                <Typography color="rgba(255,255,255,0.6)">
                  No services have been added yet.
                </Typography>
              )}
              {services.map((bookable, i) => (
                <Paper
                  key={`${bookable.service.id}-${i}`}
                  sx={{
                    p: 2,
                    bgcolor: darkCardBg,
                    border: '1px solid rgba(255,255,255,0.08)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 2,
                  }}
                >
                  <Box>
                    <Typography
                      fontWeight="bold"
                      sx={{ color: 'rgba(255,255,255,0.8)' }}
                    >
                      {bookable.service.name}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                      >
                        <AttachMoneyIcon
                          fontSize="small"
                          sx={{ color: accent }}
                        />
                        <Typography
                          variant="body2"
                          sx={{ color: 'rgba(255,255,255,0.7)' }}
                        >
                          £{bookable.service.price ?? 0}
                        </Typography>
                      </Box>
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                      >
                        <AccessTimeIcon
                          fontSize="small"
                          sx={{ color: accent }}
                        />
                        <Typography
                          variant="body2"
                          sx={{ color: 'rgba(255,255,255,0.7)' }}
                        >
                          {bookable.service.duration ?? 0} min
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  <Button
                    variant="contained"
                    onClick={() => openBookingModal(bookable)}
                    sx={{ bgcolor: accent, '&:hover': { bgcolor: '#b86e08' } }}
                  >
                    Select & Book
                  </Button>
                </Paper>
              ))}
            </Box>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper
              sx={{
                p: 3,
                bgcolor: darkCardBg,
                border: '1px solid rgba(255,255,255,0.08)',
                position: { md: 'sticky' },
                top: { md: 90 },
              }}
            >
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
              >
                <EventAvailableIcon sx={{ color: accent }} />
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  sx={{ color: 'rgba(255,255,255,0.6)' }}
                >
                  {completedBookingsCount} completed{' '}
                  {completedBookingsCount <= 1 ? 'booking' : 'bookings'}
                </Typography>
              </Box>

              {ratingSummary && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 2,
                    flexWrap: 'wrap',
                  }}
                >
                  <Rating
                    value={ratingSummary.averageRating}
                    precision={0.1}
                    readOnly
                  />
                  <Typography
                    variant="body2"
                    sx={{ color: 'rgba(255,255,255,0.6)' }}
                  >
                    {ratingSummary.averageRating.toFixed(1)} (
                    {ratingSummary.totalReviews} reviews)
                  </Typography>
                  <SampleDataBadge />
                </Box>
              )}

              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={() => openBookingModal()}
                sx={{
                  bgcolor: accent,
                  '&:hover': { bgcolor: '#b86e08' },
                  mb: 2,
                }}
              >
                Book Now
              </Button>

              {ratingSummary && ratingSummary.reviews.length > 0 && (
                <>
                  <Divider
                    sx={{ my: 2, borderColor: 'rgba(255,255,255,0.1)' }}
                  />
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 1,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{ color: 'rgba(255,255,255,0.8)' }}
                    >
                      Recent reviews
                    </Typography>
                    <SampleDataBadge />
                  </Box>
                  {ratingSummary.reviews.map((review) => (
                    <Box key={review.id} sx={{ mb: 1.5 }}>
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <Rating value={review.rating} size="small" readOnly />
                        <Typography
                          variant="caption"
                          sx={{ color: 'rgba(255,255,255,0.5)' }}
                        >
                          {review.authorName}
                        </Typography>
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{ color: 'rgba(255,255,255,0.7)' }}
                      >
                        {review.comment}
                      </Typography>
                    </Box>
                  ))}
                </>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Container>

      <BookingModal
        open={bookingOpen}
        onClose={() => setBookingOpen(false)}
        title={`Book with ${barberName}`}
        services={services}
        preselectedService={preselected}
        loadSlots={getSlots}
        onConfirm={handleConfirmBooking}
      />
    </Box>
  );
};

const PublicBarberProfile: React.FC = () => {
  const { barberId: barberIdParam } = useParams<{ barberId: string }>();
  const barberId = (() => {
    try {
      return barberIdParam ? BigInt(barberIdParam) : null;
    } catch {
      return null;
    }
  })();

  if (!barberId) {
    return (
      <Container sx={{ mt: '100px', pb: 6 }}>
        <Alert severity="error">Invalid barber profile link.</Alert>
      </Container>
    );
  }

  return (
    <PublicProfileProvider entityType="BARBER" id={barberId}>
      <PublicBarberProfileContent />
      <StickyFooter {...companyName} />
    </PublicProfileProvider>
  );
};

export default PublicBarberProfile;
